import { Lesson, Term } from '../types';

/**
 * Normalize a date value from various formats (Date, string, Firestore Timestamp) to a Date object
 * @param dateValue - The date value in any supported format
 * @returns A Date object
 */
function normalizeDate(dateValue: Date | string | { toDate: () => Date } | undefined): Date | null {
  if (!dateValue) {
    return null;
  }

  if (dateValue instanceof Date) {
    return dateValue;
  }

  if (typeof dateValue === 'string') {
    return new Date(dateValue);
  }

  // Handle Firestore Timestamp (has toDate method)
  if (
    typeof dateValue === 'object' &&
    dateValue !== null &&
    'toDate' in dateValue &&
    typeof (dateValue as { toDate: () => Date }).toDate === 'function'
  ) {
    return (dateValue as { toDate: () => Date }).toDate();
  }

  // Fallback: try to convert to Date
  try {
    return new Date(dateValue as unknown as string);
  } catch {
    return null;
  }
}

/**
 * Check if a lesson is bookable based on its term status
 * @param lesson - The lesson to check
 * @param term - The term associated with the lesson (if group lesson)
 * @returns true if the lesson is bookable, false otherwise
 */
export function isLessonBookable(
  lesson: Partial<Lesson> & { lessonType: Lesson['lessonType'] },
  term?: (Partial<Term> & { isActive: boolean; endDate?: Date | string | { toDate: () => Date } }) | null
): boolean {
  // Private lessons are always bookable (they don't have terms)
  if (lesson.lessonType === 'private') {
    return true;
  }

  // Camps are standalone — bookable as long as they haven't ended
  if (lesson.lessonType === 'camp') {
    // If the lesson has an endDate, check it hasn't passed
    if ((lesson as any).endDate) {
      const endDate = normalizeDate((lesson as any).endDate);
      if (endDate) {
        const now = new Date(Date.now());
        if (endDate < now) return false;
      }
    }
    return true;
  }

  // Group lessons need a term (date range comes from the term)
  if (!term) {
    return false;
  }

  // Check if term is active
  if (!term.isActive) {
    return false;
  }

  // Check if term has ended (past term)
  if (term.endDate) {
    const endDate = normalizeDate(term.endDate);
    if (endDate) {
      const now = new Date(Date.now());
      if (endDate < now) {
        return false;
      }
    }
  }

  return true;
}

/**
 * Get the reason why a lesson is not bookable
 * @param lesson - The lesson to check
 * @param term - The term associated with the lesson (if group lesson)
 * @returns A string explaining why the lesson is not bookable, or null if it is bookable
 */
export function getLessonBookingReason(
  lesson: Partial<Lesson> & { lessonType: Lesson['lessonType'] },
  term?: (Partial<Term> & { isActive: boolean; endDate?: Date | string | { toDate: () => Date } }) | null
): string | null {
  if (isLessonBookable(lesson, term)) {
    return null;
  }

  // Private lessons should always be bookable
  if (lesson.lessonType === 'private') {
    return null;
  }

  if (!term) {
    return 'This lesson is not associated with a valid term and cannot be booked.';
  }

  if (!term.isActive) {
    return 'This lesson is part of an inactive term and cannot be booked.';
  }

  if (term.endDate) {
    const endDate = normalizeDate(term.endDate);
    if (endDate) {
      const now = new Date(Date.now());
      if (endDate < now) {
        return 'This lesson is part of a past term and cannot be booked.';
      }
    }
  }

  return 'This lesson cannot be booked at this time.';
}

/**
 * Validates if a lesson is bookable based on its term status
 * This is a convenience function that returns an object with isValid and error
 * Useful for backend validation scenarios
 * @param lessonData - The lesson data from Firestore
 * @param termData - The term data from Firestore (if group lesson)
 * @returns Object with isValid boolean and error message if invalid
 */
export function validateLessonBookable(
  lessonData: Partial<Lesson> & { lessonType: Lesson['lessonType'] },
  termData: (Partial<Term> & { isActive: boolean; endDate?: Date | string | { toDate: () => Date } }) | null
): { isValid: boolean; error?: string } {
  const isValid = isLessonBookable(lessonData, termData);
  
  if (isValid) {
    return { isValid: true };
  }

  const error = getLessonBookingReason(lessonData, termData);
  return {
    isValid: false,
    error: error || 'This lesson cannot be booked at this time.',
  };
}

